import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Plus, MessageSquare, Pin, User, Clock, Send, Trash2, X } from "lucide-react";

const CATEGORIES = ["general", "strategy", "fundraising", "marketing", "product", "hiring", "ai-tools"];

export default function CommunityForum() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showNewThread, setShowNewThread] = useState(false);
  const [selectedThread, setSelectedThread] = useState<number | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>("");
  const [newThread, setNewThread] = useState({ title: "", content: "", category: "general" });
  const [replyContent, setReplyContent] = useState("");

  const { data: threads } = useQuery<any[]>({
    queryKey: ["/api/forum/threads", filterCategory],
    queryFn: async () => {
      const url = filterCategory ? `/api/forum/threads?category=${filterCategory}` : "/api/forum/threads";
      const res = await fetch(url, { credentials: "include" });
      if (res.status === 401) { navigate("/login"); return []; }
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
    enabled: isAuthenticated,
  });

  const { data: thread } = useQuery<any>({
    queryKey: ["/api/forum/threads", selectedThread],
    queryFn: async () => {
      const res = await fetch(`/api/forum/threads/${selectedThread}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
    enabled: !!selectedThread,
  });

  const { data: replies } = useQuery<any[]>({
    queryKey: ["/api/forum/threads", selectedThread, "replies"],
    queryFn: async () => {
      const res = await fetch(`/api/forum/threads/${selectedThread}/replies`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
    enabled: !!selectedThread,
  });

  const createThread = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch("/api/forum/threads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to create thread");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/forum/threads"] });
      setShowNewThread(false);
      setNewThread({ title: "", content: "", category: "general" });
      toast({ title: "Thread created" });
    },
  });

  const createReply = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch(`/api/forum/threads/${selectedThread}/replies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to post reply");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/forum/threads", selectedThread, "replies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/forum/threads"] });
      setReplyContent("");
    },
  });

  const deleteThread = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/forum/threads/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/forum/threads"] });
      setSelectedThread(null);
      toast({ title: "Thread deleted" });
    },
  });

  if (isLoading) return <><Navbar /><main id="main-content" className="pt-20 min-h-screen flex items-center justify-center"><div className="text-muted-foreground" role="status" aria-live="polite">Loading...</div></main></>;
  if (!isAuthenticated) { navigate("/login"); return null; }

  if (selectedThread && thread) {
    return (
      <>
        <Navbar />
        <main id="main-content" className="pt-20 min-h-screen">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
            <Button variant="ghost" className="mb-8 -ml-4 text-muted-foreground" onClick={() => setSelectedThread(null)}>
              <ArrowLeft size={16} className="mr-2" aria-hidden="true" /> Back to Forum
            </Button>

            <div className="mb-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Badge variant="secondary" className="mb-3 bg-white/5">{thread.category}</Badge>
                  <h1 className="text-3xl font-display font-bold mb-4">{thread.title}</h1>
                </div>
                {(thread.userId === user?.id || user?.isAdmin) && (
                  <Button variant="ghost" size="sm" className="text-red-400" aria-label="Delete thread" onClick={() => { if (confirm("Delete this thread?")) deleteThread.mutate(thread.id); }}>
                    <Trash2 size={16} aria-hidden="true" />
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                <span className="flex items-center gap-1"><User size={14} aria-hidden="true" /> {thread.userName}</span>
                <span className="flex items-center gap-1"><Clock size={14} aria-hidden="true" /> {new Date(thread.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="bg-card border border-white/10 rounded-xl p-6">
                <p className="whitespace-pre-wrap text-foreground leading-relaxed">{thread.content}</p>
              </div>
            </div>

            <h2 className="text-lg font-semibold mb-4">{replies?.length || 0} {replies?.length === 1 ? "Reply" : "Replies"}</h2>

            <div className="space-y-4 mb-8" role="list" aria-label="Thread replies">
              {replies?.map((reply: any) => (
                <article key={reply.id} className="bg-card/50 border border-white/5 rounded-xl p-5" role="listitem">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-xs font-bold" aria-hidden="true">
                      {reply.userName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <span className="text-sm font-medium">{reply.userName}</span>
                      <time className="text-xs text-muted-foreground ml-2" dateTime={reply.createdAt}>{new Date(reply.createdAt).toLocaleDateString()}</time>
                    </div>
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-muted-foreground leading-relaxed">{reply.content}</p>
                </article>
              ))}
            </div>

            <form
              className="flex gap-3"
              aria-label="Reply to thread"
              onSubmit={(e) => { e.preventDefault(); if (replyContent.trim()) createReply.mutate({ content: replyContent }); }}
            >
              <Textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write a reply..."
                className="bg-card border-white/10 min-h-[60px]"
                aria-label="Write a reply"
                data-testid="input-reply"
              />
              <Button type="submit" className="shrink-0 self-end" disabled={!replyContent.trim() || createReply.isPending} aria-label="Post reply" aria-busy={createReply.isPending} data-testid="button-post-reply">
                <Send size={16} aria-hidden="true" />
              </Button>
            </form>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <Button variant="ghost" className="mb-6 -ml-4 text-muted-foreground" onClick={() => navigate("/community")}>
            <ArrowLeft size={16} className="mr-2" aria-hidden="true" /> Community Hub
          </Button>

          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-display font-bold mb-2">Forum</h1>
              <p className="text-muted-foreground">Discuss, ask questions, and share insights.</p>
            </div>
            <Button onClick={() => setShowNewThread(true)} data-testid="button-new-thread">
              <Plus size={18} className="mr-2" aria-hidden="true" /> New Thread
            </Button>
          </div>

          <div className="flex gap-2 flex-wrap mb-8" role="group" aria-label="Filter by category">
            <Badge
              className={`cursor-pointer ${!filterCategory ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground hover:text-foreground"}`}
              onClick={() => setFilterCategory("")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFilterCategory(""); } }}
              aria-pressed={!filterCategory}
            >
              All
            </Badge>
            {CATEGORIES.map((cat) => (
              <Badge
                key={cat}
                className={`cursor-pointer capitalize ${filterCategory === cat ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground hover:text-foreground"}`}
                onClick={() => setFilterCategory(cat)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFilterCategory(cat); } }}
                aria-pressed={filterCategory === cat}
              >
                {cat}
              </Badge>
            ))}
          </div>

          {showNewThread && (
            <Card className="border-white/10 p-6 mb-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-semibold text-lg">Start a New Discussion</h2>
                <button onClick={() => setShowNewThread(false)} aria-label="Close new thread form" className="p-3 -m-2 rounded-md hover:bg-white/5 transition-colors"><X size={18} className="text-muted-foreground" aria-hidden="true" /></button>
              </div>
              <form
                className="space-y-4"
                aria-label="Create new thread"
                onSubmit={(e) => { e.preventDefault(); createThread.mutate(newThread); }}
              >
                <div className="space-y-2">
                  <Label htmlFor="thread-title">Title</Label>
                  <Input
                    id="thread-title"
                    value={newThread.title}
                    onChange={(e) => setNewThread({ ...newThread, title: e.target.value })}
                    placeholder="What's on your mind?"
                    className="bg-background border-white/10"
                    data-testid="input-thread-title"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="thread-category">Category</Label>
                  <select
                    id="thread-category"
                    value={newThread.category}
                    onChange={(e) => setNewThread({ ...newThread, category: e.target.value })}
                    className="w-full bg-background border border-white/10 rounded-md px-3 py-2.5 text-sm min-h-[44px]"
                    data-testid="select-thread-category"
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat} className="capitalize">{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="thread-content">Content</Label>
                  <Textarea
                    id="thread-content"
                    value={newThread.content}
                    onChange={(e) => setNewThread({ ...newThread, content: e.target.value })}
                    placeholder="Share your thoughts..."
                    rows={5}
                    className="bg-background border-white/10"
                    data-testid="input-thread-content"
                    required
                  />
                </div>
                <Button type="submit" disabled={createThread.isPending} aria-busy={createThread.isPending} data-testid="button-create-thread">
                  {createThread.isPending ? "Posting..." : "Post Thread"}
                </Button>
              </form>
            </Card>
          )}

          <div className="space-y-3">
            {threads && threads.length > 0 ? (
              threads.map((t: any) => (
                <Card
                  key={t.id}
                  className="border-white/5 p-5 hover:border-primary/30 transition-colors cursor-pointer group"
                  onClick={() => setSelectedThread(t.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedThread(t.id); } }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Thread: ${t.title}`}
                  data-testid={`card-thread-${t.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        {t.pinned && <Pin size={14} className="text-primary" aria-hidden="true" />}
                        <Badge variant="secondary" className="bg-white/5 text-xs capitalize">{t.category}</Badge>
                      </div>
                      <h2 className="font-semibold text-base group-hover:text-primary transition-colors truncate">{t.title}</h2>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span>{t.userName}</span>
                        <span>{new Date(t.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground shrink-0">
                      <MessageSquare size={16} aria-hidden="true" />
                      <span>{t.replyCount}</span>
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <div className="text-center py-16 bg-card border border-white/5 rounded-2xl">
                <MessageSquare size={32} className="mx-auto mb-4 text-muted-foreground" aria-hidden="true" />
                <h2 className="font-semibold mb-2">No threads yet</h2>
                <p className="text-muted-foreground text-sm">Be the first to start a discussion!</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
